import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminApi, subscriptionTypesApi, accountsApi } from '../lib/api';
import { formatCurrency, formatDate, formatShortDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  LayoutDashboard, Package, Server, Users, Settings, LogOut,
  Plus, Edit, Trash2, Check, Eye,
  Loader2, Shield, Menu, X, TrendingUp, AlertTriangle
} from 'lucide-react';

export default function AdminPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Dashboard stats
  const [stats, setStats] = useState(null);
  
  // Subscription types
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [typeForm, setTypeForm] = useState({
    name: '', description: '', price: '', max_users_per_account: '', icon_url: ''
  });
  
  // Accounts
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [accountForm, setAccountForm] = useState({
    subscription_type_id: '', email: '', password: ''
  });
  const [accountUsersDialogOpen, setAccountUsersDialogOpen] = useState(false);
  const [accountUsers, setAccountUsers] = useState([]);
  const [moveUserDialogOpen, setMoveUserDialogOpen] = useState(false);
  const [moveUserData, setMoveUserData] = useState({ user_id: '', from_account_id: '', to_account_id: '' });
  
  // Users
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  
  // MFA
  const [mfaStatus, setMfaStatus] = useState(false);
  const [mfaEnrollData, setMfaEnrollData] = useState(null);
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [mfaVerifyCode, setMfaVerifyCode] = useState('');

  // Delete confirmation dialogs
  const [deleteTypeDialogOpen, setDeleteTypeDialogOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Disable MFA dialog
  const [disableMfaDialogOpen, setDisableMfaDialogOpen] = useState(false);
  const [disableMfaCode, setDisableMfaCode] = useState('');
  const [disableMfaLoading, setDisableMfaLoading] = useState(false);
  const [disableMfaError, setDisableMfaError] = useState(null);

  const navItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'subscriptions', icon: Package, label: 'Abonnements' },
    { id: 'accounts', icon: Server, label: 'Comptes' },
    { id: 'users', icon: Users, label: 'Utilisateurs' },
    { id: 'settings', icon: Settings, label: 'Paramètres' }
  ];

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      setIsAuthenticated(true);
      fetchData();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, typesRes, accountsRes, usersRes, mfaRes] = await Promise.all([
        adminApi.getDashboardStats(),
        subscriptionTypesApi.getAll(true),
        accountsApi.getAll(),
        adminApi.getUsers(),
        adminApi.getMfaStatus()
      ]);
      setStats(statsRes.data);
      setSubscriptionTypes(typesRes.data);
      setAccounts(accountsRes.data);
      setUsers(usersRes.data);
      setMfaStatus(mfaRes.data.is_enabled);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const response = await adminApi.login(loginEmail, loginPassword, mfaRequired ? totpCode : null);
      
      if (response.data.mfa_required && !mfaRequired) {
        setMfaRequired(true);
        setLoginLoading(false);
        return;
      }
      
      localStorage.setItem('admin_token', response.data.access_token);
      localStorage.setItem('access_token', response.data.access_token);
      setIsAuthenticated(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.invalidCredentials'));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('access_token');
    setIsAuthenticated(false);
  };

  // Subscription Type handlers
  const handleCreateType = async () => {
    try {
      await subscriptionTypesApi.create({
        ...typeForm,
        price: parseFloat(typeForm.price),
        max_users_per_account: parseInt(typeForm.max_users_per_account)
      });
      toast.success(t('common.success'));
      setTypeDialogOpen(false);
      setTypeForm({ name: '', description: '', price: '', max_users_per_account: '', icon_url: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleUpdateType = async () => {
    try {
      const updateData = {};
      if (typeForm.name) updateData.name = typeForm.name;
      if (typeForm.description) updateData.description = typeForm.description;
      if (typeForm.price) updateData.price = parseFloat(typeForm.price);
      if (typeForm.max_users_per_account) updateData.max_users_per_account = parseInt(typeForm.max_users_per_account);
      if (typeForm.icon_url !== undefined) updateData.icon_url = typeForm.icon_url;
      
      await subscriptionTypesApi.update(selectedType.id, updateData);
      toast.success(t('common.success'));
      setTypeDialogOpen(false);
      setSelectedType(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleToggleTypeStatus = async (type) => {
    try {
      await subscriptionTypesApi.update(type.id, { is_active: !type.is_active });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleDeleteType = async (id) => {
    // Show confirmation dialog
    const type = subscriptionTypes.find(t => t.id === id);
    setTypeToDelete(type);
    setDeleteError(null);
    setDeleteTypeDialogOpen(true);
  };

  const confirmDeleteType = async () => {
    if (!typeToDelete) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await subscriptionTypesApi.delete(typeToDelete.id);
      toast.success('Type d\'abonnement supprimé');
      setDeleteTypeDialogOpen(false);
      setTypeToDelete(null);
      fetchData();
    } catch (error) {
      setDeleteError(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Account handlers
  const handleCreateAccount = async () => {
    try {
      await accountsApi.create(accountForm);
      toast.success(t('common.success'));
      setAccountDialogOpen(false);
      setAccountForm({ subscription_type_id: '', email: '', password: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleUpdateAccount = async () => {
    try {
      const updateData = {};
      if (accountForm.email) updateData.email = accountForm.email;
      if (accountForm.password) updateData.password = accountForm.password;
      
      await accountsApi.update(selectedAccount.id, updateData);
      toast.success(t('common.success'));
      setAccountDialogOpen(false);
      setSelectedAccount(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleMarkAccountNormal = async (accountId) => {
    try {
      await accountsApi.update(accountId, { status: 'normal' });
      toast.success(t('common.success'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleDeleteAccount = async (id) => {
    // Show confirmation dialog
    const account = accounts.find(a => a.id === id);
    setAccountToDelete(account);
    setDeleteError(null);
    setDeleteAccountDialogOpen(true);
  };

  const confirmDeleteAccount = async () => {
    if (!accountToDelete) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await accountsApi.delete(accountToDelete.id);
      toast.success('Compte supprimé');
      setDeleteAccountDialogOpen(false);
      setAccountToDelete(null);
      fetchData();
    } catch (error) {
      setDeleteError(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleViewAccountUsers = async (account) => {
    try {
      const response = await accountsApi.getUsers(account.id);
      setAccountUsers(response.data);
      setSelectedAccount(account);
      setAccountUsersDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleMoveUser = async () => {
    try {
      await accountsApi.moveUser(moveUserData);
      toast.success(t('common.success'));
      setMoveUserDialogOpen(false);
      setMoveUserData({ user_id: '', from_account_id: '', to_account_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  // MFA handlers
  const handleEnrollMfa = async () => {
    try {
      const response = await adminApi.enrollMfa();
      setMfaEnrollData(response.data);
      setMfaDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleVerifyMfa = async () => {
    try {
      await adminApi.verifyMfa(mfaVerifyCode);
      toast.success('2FA activée avec succès');
      setMfaDialogOpen(false);
      setMfaEnrollData(null);
      setMfaVerifyCode('');
      setMfaStatus(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleDisableMfa = async () => {
    // Open dialog instead of window.prompt
    setDisableMfaCode('');
    setDisableMfaError(null);
    setDisableMfaDialogOpen(true);
  };

  const confirmDisableMfa = async () => {
    if (disableMfaCode.length !== 6) {
      setDisableMfaError('Le code doit contenir 6 chiffres');
      return;
    }
    setDisableMfaLoading(true);
    setDisableMfaError(null);
    try {
      await adminApi.disableMfa(disableMfaCode);
      toast.success('2FA désactivée');
      setMfaStatus(false);
      setDisableMfaDialogOpen(false);
      setDisableMfaCode('');
    } catch (error) {
      setDisableMfaError(error.response?.data?.detail || 'Code invalide');
    } finally {
      setDisableMfaLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'normal':
        return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Normal</Badge>;
      case 'needs_password_change':
        return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">Changer mdp</Badge>;
      case 'needs_user_reallocation':
        return <Badge className="bg-red-500/10 text-red-600 border-red-500/20">Réaffectation</Badge>;
      default:
        return null;
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-muted/30 to-background p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-border">
            <div className="text-center mb-8">
              <Link to="/" className="font-heading text-3xl font-black text-primary">izyko</Link>
              <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full">
                <Shield className="h-4 w-4 text-primary" />
                <span className="text-sm font-bold text-primary">Admin</span>
              </div>
              <h1 className="font-heading text-2xl font-bold mt-6">{t('admin.login.title')}</h1>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label className="font-bold">Email</Label>
                <Input
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                  disabled={mfaRequired}
                  className="rounded-xl h-12"
                  data-testid="admin-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-bold">Mot de passe</Label>
                <Input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                  disabled={mfaRequired}
                  className="rounded-xl h-12"
                  data-testid="admin-password-input"
                />
              </div>
              
              {mfaRequired && (
                <div className="space-y-2">
                  <Label className="font-bold">{t('admin.login.totpCode')}</Label>
                  <Input
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    placeholder="000000"
                    maxLength={6}
                    className="rounded-xl h-12 text-center text-2xl font-mono tracking-widest"
                    data-testid="admin-totp-input"
                  />
                  <p className="text-sm text-muted-foreground text-center">{t('admin.login.mfaRequired')}</p>
                </div>
              )}
              
              <Button type="submit" className="w-full rounded-full h-12 text-base font-bold" disabled={loginLoading} data-testid="admin-login-btn">
                {loginLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('auth.login.submit')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-muted-foreground hover:text-foreground font-medium">
                Retour au site
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <span className="font-bold text-muted-foreground">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/30 to-background" data-testid="admin-dashboard">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-full w-64 bg-white border-r-2 border-border flex-col z-50">
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <Link to="/" className="font-heading text-2xl font-black text-primary">izyko</Link>
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full">
            <Shield className="h-3 w-3 text-primary" />
            <span className="text-xs font-bold text-primary">Admin</span>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm transition-all ${
                    activeTab === item.id 
                      ? 'bg-primary text-white shadow-lg' 
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                  data-testid={`admin-nav-${item.id}`}
                >
                  <item.icon className="h-5 w-5" strokeWidth={activeTab === item.id ? 2.5 : 2} />
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-border">
          <Button 
            variant="outline" 
            className="w-full rounded-xl justify-start" 
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" strokeWidth={2} />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 glass border-b-2 border-border/40 px-4 py-4 safe-top">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 rounded-xl hover:bg-muted"
          >
            <Menu className="h-6 w-6" strokeWidth={2} />
          </button>
          <div className="flex items-center gap-2">
            <span className="font-heading text-xl font-black">izyko</span>
            <Badge variant="secondary" className="text-xs">Admin</Badge>
          </div>
          <div className="w-10" />
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-white flex flex-col animate-in slide-in-from-left">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <span className="font-heading text-xl font-black text-primary">izyko</span>
                <Badge variant="secondary" className="ml-2 text-xs">Admin</Badge>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-xl hover:bg-muted">
                <X className="h-5 w-5" strokeWidth={2} />
              </button>
            </div>
            
            <nav className="flex-1 p-4">
              <ul className="space-y-2">
                {navItems.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold transition-all ${
                        activeTab === item.id 
                          ? 'bg-primary text-white' 
                          : 'text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      <item.icon className="h-5 w-5" strokeWidth={2} />
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="p-4 border-t border-border">
              <Button variant="outline" className="w-full rounded-xl justify-start" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" strokeWidth={2} />
                Déconnexion
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-8">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <>
              <div className="mb-8">
                <h1 className="font-heading text-2xl font-black mb-2">Dashboard</h1>
                <p className="text-muted-foreground font-medium">Vue d'ensemble de la plateforme</p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="bg-white rounded-2xl p-6 shadow-soft border-2 border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Users className="h-5 w-5 text-blue-600" strokeWidth={2} />
                    </div>
                    <span className="text-sm font-bold text-muted-foreground">Utilisateurs</span>
                  </div>
                  <p className="font-heading text-3xl font-black">{stats?.total_users || 0}</p>
                </div>
                
                <div className="bg-white rounded-2xl p-6 shadow-soft border-2 border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-green-600" strokeWidth={2} />
                    </div>
                    <span className="text-sm font-bold text-muted-foreground">Revenus</span>
                  </div>
                  <p className="font-heading text-3xl font-black">{formatCurrency(stats?.total_revenue || 0)}</p>
                </div>
                
                <div className="bg-white rounded-2xl p-6 shadow-soft border-2 border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Package className="h-5 w-5 text-purple-600" strokeWidth={2} />
                    </div>
                    <span className="text-sm font-bold text-muted-foreground">Abonnements</span>
                  </div>
                  <p className="font-heading text-3xl font-black">{stats?.active_subscriptions || 0}</p>
                </div>
                
                <div className="bg-white rounded-2xl p-6 shadow-soft border-2 border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                      <AlertTriangle className="h-5 w-5 text-yellow-600" strokeWidth={2} />
                    </div>
                    <span className="text-sm font-bold text-muted-foreground">À traiter</span>
                  </div>
                  <p className="font-heading text-3xl font-black text-yellow-600">{stats?.accounts_needing_attention || 0}</p>
                </div>
              </div>

              {/* Quick Overview Tables */}
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-6 shadow-soft border-2 border-border">
                  <h3 className="font-heading font-bold mb-4">Types d'abonnements</h3>
                  <div className="space-y-3">
                    {subscriptionTypes.slice(0, 4).map((type) => (
                      <div key={type.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                            <span className="font-bold text-sm">{type.name[0]}</span>
                          </div>
                          <span className="font-bold">{type.name}</span>
                        </div>
                        <Badge variant={type.is_active ? 'default' : 'secondary'}>
                          {type.is_active ? 'Actif' : 'Inactif'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                  <Button variant="ghost" className="w-full mt-4 rounded-full" onClick={() => setActiveTab('subscriptions')}>
                    Voir tout
                  </Button>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-soft border-2 border-border">
                  <h3 className="font-heading font-bold mb-4">Comptes nécessitant attention</h3>
                  <div className="space-y-3">
                    {accounts.filter(a => a.status !== 'normal').slice(0, 4).map((account) => (
                      <div key={account.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                        <span className="font-medium text-sm truncate max-w-[150px]">{account.email}</span>
                        {getStatusBadge(account.status)}
                      </div>
                    ))}
                    {accounts.filter(a => a.status !== 'normal').length === 0 && (
                      <p className="text-muted-foreground text-center py-4">Tout est en ordre!</p>
                    )}
                  </div>
                  <Button variant="ghost" className="w-full mt-4 rounded-full" onClick={() => setActiveTab('accounts')}>
                    Voir tout
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Subscription Types Tab */}
          {activeTab === 'subscriptions' && (
            <>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="font-heading text-2xl font-black">{t('admin.subscriptionTypes.title')}</h1>
                  <p className="text-muted-foreground font-medium">Gérez les types d'abonnements disponibles</p>
                </div>
                <Button className="rounded-full" onClick={() => { setSelectedType(null); setTypeForm({ name: '', description: '', price: '', max_users_per_account: '', icon_url: '' }); setTypeDialogOpen(true); }} data-testid="create-type-btn">
                  <Plus className="h-4 w-4 mr-2" strokeWidth={2} />
                  Nouveau
                </Button>
              </div>
              
              <div className="bg-white rounded-2xl shadow-soft border-2 border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="text-left py-4 px-4 font-bold text-sm">Nom</th>
                        <th className="text-left py-4 px-4 font-bold text-sm">Prix</th>
                        <th className="text-left py-4 px-4 font-bold text-sm">Max utilisateurs</th>
                        <th className="text-left py-4 px-4 font-bold text-sm">Abonnés</th>
                        <th className="text-left py-4 px-4 font-bold text-sm">Comptes</th>
                        <th className="text-left py-4 px-4 font-bold text-sm">Statut</th>
                        <th className="text-right py-4 px-4 font-bold text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptionTypes.map((type) => (
                        <tr key={type.id} className="border-t border-border/50 hover:bg-muted/30" data-testid={`type-row-${type.id}`}>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                                <span className="font-heading font-bold">{type.name[0]}</span>
                              </div>
                              <span className="font-bold">{type.name}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 font-mono font-bold">{formatCurrency(type.price)}</td>
                          <td className="py-4 px-4">{type.max_users_per_account}</td>
                          <td className="py-4 px-4">{type.total_subscribers}</td>
                          <td className="py-4 px-4">{type.total_accounts}</td>
                          <td className="py-4 px-4">
                            <Switch 
                              checked={type.is_active} 
                              onCheckedChange={() => handleToggleTypeStatus(type)}
                              data-testid={`toggle-type-${type.id}`}
                            />
                          </td>
                          <td className="py-4 px-4 text-right">
                            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => { setSelectedType(type); setTypeForm(type); setTypeDialogOpen(true); }}>
                              <Edit className="h-4 w-4" strokeWidth={2} />
                            </Button>
                            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => handleDeleteType(type.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" strokeWidth={2} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Accounts Tab */}
          {activeTab === 'accounts' && (
            <>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="font-heading text-2xl font-black">{t('admin.accounts.title')}</h1>
                  <p className="text-muted-foreground font-medium">Gérez les comptes de service partagés</p>
                </div>
                <Button className="rounded-full" onClick={() => { setSelectedAccount(null); setAccountForm({ subscription_type_id: '', email: '', password: '' }); setAccountDialogOpen(true); }} data-testid="create-account-btn">
                  <Plus className="h-4 w-4 mr-2" strokeWidth={2} />
                  Nouveau
                </Button>
              </div>
              
              <div className="bg-white rounded-2xl shadow-soft border-2 border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="text-left py-4 px-4 font-bold text-sm">Type</th>
                        <th className="text-left py-4 px-4 font-bold text-sm">Email</th>
                        <th className="text-left py-4 px-4 font-bold text-sm">Statut</th>
                        <th className="text-left py-4 px-4 font-bold text-sm">Utilisateurs</th>
                        <th className="text-right py-4 px-4 font-bold text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accounts.map((account) => {
                        const type = subscriptionTypes.find(t => t.id === account.subscription_type_id);
                        return (
                          <tr key={account.id} className={`border-t border-border/50 hover:bg-muted/30 ${account.status !== 'normal' ? 'bg-yellow-50/50' : ''}`} data-testid={`account-row-${account.id}`}>
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                                  <span className="font-bold text-sm">{type?.name[0] || '?'}</span>
                                </div>
                                <span className="font-bold">{type?.name || '-'}</span>
                              </div>
                            </td>
                            <td className="py-4 px-4 font-mono text-sm">{account.email}</td>
                            <td className="py-4 px-4">{getStatusBadge(account.status)}</td>
                            <td className="py-4 px-4">
                              <span className={account.assigned_users_count >= account.max_users ? 'text-yellow-600 font-bold' : ''}>
                                {account.assigned_users_count}/{account.max_users}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-right space-x-1">
                              <Button variant="ghost" size="icon" className="rounded-full" onClick={() => handleViewAccountUsers(account)} title="Voir utilisateurs">
                                <Eye className="h-4 w-4" strokeWidth={2} />
                              </Button>
                              {account.status === 'needs_password_change' && (
                                <Button variant="ghost" size="icon" className="rounded-full" onClick={() => { setSelectedAccount(account); setAccountForm({ email: account.email, password: '' }); setAccountDialogOpen(true); }} title="Changer mot de passe">
                                  <Edit className="h-4 w-4 text-yellow-600" strokeWidth={2} />
                                </Button>
                              )}
                              {account.status !== 'normal' && account.status !== 'needs_user_reallocation' && (
                                <Button variant="ghost" size="icon" className="rounded-full" onClick={() => handleMarkAccountNormal(account.id)} title="Marquer normal">
                                  <Check className="h-4 w-4 text-green-600" strokeWidth={2} />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="rounded-full" onClick={() => handleDeleteAccount(account.id)}>
                                <Trash2 className="h-4 w-4 text-destructive" strokeWidth={2} />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h1 className="font-heading text-2xl font-black">{t('admin.users.title')}</h1>
                  <p className="text-muted-foreground font-medium">Consultez tous les utilisateurs</p>
                </div>
                <Input 
                  placeholder="Rechercher..." 
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="w-full sm:w-64 rounded-full"
                />
              </div>
              
              <div className="bg-white rounded-2xl shadow-soft border-2 border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="text-left py-4 px-4 font-bold text-sm">Nom</th>
                        <th className="text-left py-4 px-4 font-bold text-sm">Email</th>
                        <th className="text-left py-4 px-4 font-bold text-sm">Solde</th>
                        <th className="text-left py-4 px-4 font-bold text-sm">Abonnements</th>
                        <th className="text-left py-4 px-4 font-bold text-sm">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.filter(u => 
                        !userSearch || 
                        u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
                        u.first_name.toLowerCase().includes(userSearch.toLowerCase()) ||
                        u.last_name.toLowerCase().includes(userSearch.toLowerCase())
                      ).map((user) => (
                        <tr key={user.id} className="border-t border-border/50 hover:bg-muted/30">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                                <span className="font-bold">{user.first_name[0]}</span>
                              </div>
                              <span className="font-bold">{user.first_name} {user.last_name}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-sm">{user.email}</td>
                          <td className="py-4 px-4 font-mono font-bold">{formatCurrency(user.wallet_balance)}</td>
                          <td className="py-4 px-4">
                            <Badge variant="secondary">{user.active_subscriptions}</Badge>
                          </td>
                          <td className="py-4 px-4 text-sm text-muted-foreground">{formatShortDate(user.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <>
              <div className="mb-6">
                <h1 className="font-heading text-2xl font-black">Paramètres</h1>
                <p className="text-muted-foreground font-medium">Configurez votre compte admin</p>
              </div>

              <div className="max-w-xl">
                <div className="bg-white rounded-2xl p-6 shadow-soft border-2 border-border">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <Shield className="h-6 w-6 text-primary" strokeWidth={2} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold">{t('admin.mfa.title')}</h3>
                      <p className="text-sm text-muted-foreground">
                        {mfaStatus ? 'Votre compte est protégé' : 'Activez 2FA pour plus de sécurité'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${mfaStatus ? 'bg-green-500' : 'bg-yellow-500'}`} />
                      <span className="font-bold">{mfaStatus ? t('admin.mfa.enabled') : t('admin.mfa.disabled')}</span>
                    </div>
                    {mfaStatus ? (
                      <Button variant="outline" className="rounded-full" onClick={handleDisableMfa}>
                        {t('admin.mfa.disable')}
                      </Button>
                    ) : (
                      <Button className="rounded-full" onClick={handleEnrollMfa}>
                        {t('admin.mfa.enable')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Subscription Type Dialog */}
      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{selectedType ? 'Modifier' : 'Créer'} un type d'abonnement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-bold">{t('admin.subscriptionTypes.name')}</Label>
              <Input className="rounded-xl" value={typeForm.name} onChange={(e) => setTypeForm({...typeForm, name: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label className="font-bold">Description</Label>
              <Input className="rounded-xl" value={typeForm.description || ''} onChange={(e) => setTypeForm({...typeForm, description: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-bold">{t('admin.subscriptionTypes.price')} (HTG)</Label>
                <Input className="rounded-xl" type="number" value={typeForm.price} onChange={(e) => setTypeForm({...typeForm, price: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label className="font-bold">{t('admin.subscriptionTypes.maxUsers')}</Label>
                <Input className="rounded-xl" type="number" value={typeForm.max_users_per_account} onChange={(e) => setTypeForm({...typeForm, max_users_per_account: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-bold">URL Icône (optionnel)</Label>
              <Input className="rounded-xl" value={typeForm.icon_url || ''} onChange={(e) => setTypeForm({...typeForm, icon_url: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setTypeDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button className="rounded-full" onClick={selectedType ? handleUpdateType : handleCreateType}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Account Dialog */}
      <Dialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{selectedAccount ? 'Modifier le compte' : 'Créer un compte'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!selectedAccount && (
              <div className="space-y-2">
                <Label className="font-bold">Type d'abonnement</Label>
                <Select value={accountForm.subscription_type_id} onValueChange={(v) => setAccountForm({...accountForm, subscription_type_id: v})}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {subscriptionTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id} className="rounded-lg">{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label className="font-bold">{t('admin.accounts.email')}</Label>
              <Input className="rounded-xl" value={accountForm.email} onChange={(e) => setAccountForm({...accountForm, email: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label className="font-bold">{t('admin.accounts.password')}</Label>
              <Input className="rounded-xl" type="text" value={accountForm.password} onChange={(e) => setAccountForm({...accountForm, password: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAccountDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button className="rounded-full" onClick={selectedAccount ? handleUpdateAccount : handleCreateAccount}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Account Users Dialog */}
      <Dialog open={accountUsersDialogOpen} onOpenChange={setAccountUsersDialogOpen}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Utilisateurs assignés</DialogTitle>
            <DialogDescription>{selectedAccount?.email}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {accountUsers.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">Aucun utilisateur assigné</p>
            ) : (
              <div className="space-y-2">
                {accountUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                    <div>
                      <p className="font-bold">{user.user_name}</p>
                      <p className="text-sm text-muted-foreground">{user.user_email}</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="rounded-full"
                      onClick={() => {
                        setMoveUserData({
                          user_id: user.user_id,
                          from_account_id: selectedAccount.id,
                          to_account_id: ''
                        });
                        setMoveUserDialogOpen(true);
                      }}
                    >
                      Déplacer
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Move User Dialog */}
      <Dialog open={moveUserDialogOpen} onOpenChange={setMoveUserDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Déplacer l'utilisateur</DialogTitle>
            <DialogDescription>Sélectionnez le compte de destination</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={moveUserData.to_account_id} onValueChange={(v) => setMoveUserData({...moveUserData, to_account_id: v})}>
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder="Sélectionner le compte" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {accounts.filter(a => 
                  a.id !== moveUserData.from_account_id && 
                  a.subscription_type_id === selectedAccount?.subscription_type_id &&
                  a.assigned_users_count < a.max_users
                ).map((account) => (
                  <SelectItem key={account.id} value={account.id} className="rounded-lg">
                    {account.email} ({account.assigned_users_count}/{account.max_users})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setMoveUserDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button className="rounded-full" onClick={handleMoveUser} disabled={!moveUserData.to_account_id}>{t('common.confirm')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MFA Enrollment Dialog */}
      <Dialog open={mfaDialogOpen} onOpenChange={setMfaDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{t('admin.mfa.enable')}</DialogTitle>
            <DialogDescription>{t('admin.mfa.scanQR')}</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {mfaEnrollData && (
              <>
                <div className="flex justify-center">
                  <img src={mfaEnrollData.qr_code_url} alt="QR Code" className="w-48 h-48 rounded-xl" />
                </div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">Ou entrez ce code manuellement:</p>
                  <code className="font-mono bg-muted px-3 py-2 rounded-lg text-sm">{mfaEnrollData.secret}</code>
                </div>
                <div className="space-y-2">
                  <Label className="font-bold">{t('admin.mfa.enterCode')}</Label>
                  <Input
                    value={mfaVerifyCode}
                    onChange={(e) => setMfaVerifyCode(e.target.value)}
                    placeholder="000000"
                    maxLength={6}
                    className="rounded-xl text-center text-2xl font-mono tracking-widest"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setMfaDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button className="rounded-full" onClick={handleVerifyMfa} disabled={mfaVerifyCode.length !== 6}>{t('common.confirm')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Subscription Type Dialog */}
      <Dialog open={deleteTypeDialogOpen} onOpenChange={(open) => { if (!open) { setDeleteError(null); } setDeleteTypeDialogOpen(open); }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-destructive">Supprimer l'abonnement</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer <strong>{typeToDelete?.name}</strong> ?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {deleteError && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                <div>
                  <p className="font-bold text-red-800">Suppression impossible</p>
                  <p className="text-sm text-red-700 mt-1">{deleteError}</p>
                </div>
              </div>
            )}
            {!deleteError && (
              <p className="text-muted-foreground">
                Cette action est irréversible. Assurez-vous qu'il n'y a plus de comptes associés à cet abonnement.
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => { setDeleteTypeDialogOpen(false); setTypeToDelete(null); setDeleteError(null); }}>
              {deleteError ? 'Fermer' : 'Annuler'}
            </Button>
            {!deleteError && (
              <Button variant="destructive" className="rounded-full" onClick={confirmDeleteType} disabled={deleteLoading}>
                {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Supprimer'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={deleteAccountDialogOpen} onOpenChange={(open) => { if (!open) { setDeleteError(null); } setDeleteAccountDialogOpen(open); }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-destructive">Supprimer le compte</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer le compte <strong>{accountToDelete?.email}</strong> ?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {deleteError && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                <div>
                  <p className="font-bold text-red-800">Suppression impossible</p>
                  <p className="text-sm text-red-700 mt-1">{deleteError}</p>
                </div>
              </div>
            )}
            {!deleteError && (
              <p className="text-muted-foreground">
                Cette action est irréversible. Assurez-vous qu'il n'y a plus d'utilisateurs affectés à ce compte.
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => { setDeleteAccountDialogOpen(false); setAccountToDelete(null); setDeleteError(null); }}>
              {deleteError ? 'Fermer' : 'Annuler'}
            </Button>
            {!deleteError && (
              <Button variant="destructive" className="rounded-full" onClick={confirmDeleteAccount} disabled={deleteLoading}>
                {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Supprimer'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable MFA Dialog */}
      <Dialog open={disableMfaDialogOpen} onOpenChange={(open) => { if (!open) { setDisableMfaError(null); setDisableMfaCode(''); } setDisableMfaDialogOpen(open); }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Désactiver la double authentification</DialogTitle>
            <DialogDescription>
              Entrez votre code 2FA pour confirmer la désactivation
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label className="font-bold">Code 2FA</Label>
              <Input
                type="text"
                value={disableMfaCode}
                onChange={(e) => { setDisableMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setDisableMfaError(null); }}
                placeholder="000000"
                maxLength={6}
                className="text-center text-2xl font-mono tracking-[0.5em] h-14"
                data-testid="disable-mfa-code-input"
              />
            </div>
            {disableMfaError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" strokeWidth={2} />
                <p className="text-sm text-red-700">{disableMfaError}</p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => { setDisableMfaDialogOpen(false); setDisableMfaCode(''); setDisableMfaError(null); }}>
              Annuler
            </Button>
            <Button variant="destructive" className="rounded-full" onClick={confirmDisableMfa} disabled={disableMfaLoading || disableMfaCode.length !== 6}>
              {disableMfaLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Désactiver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
